import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles, UserRound } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GREETING =
  "Welcome. I'm the Melano concierge. Ask me about our work, the four brands, or In Common, and I'll help you find your way.";

const STARTERS = [
  "What does Melano International do?",
  "Tell me about your four brands",
  "What is In Common?",
  "How do I get in touch?",
];

const PERSON_HINTS = ["human", "real person", "speak to", "talk to someone", "talk to a person", "speak with", "the team", "a person"];

export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() =>
    (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : String(Date.now()))
  );
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState(null); // null | 'form' | 'done'
  const [hform, setHform] = useState({ name: "", email: "", question: "" });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, handoff]);

  const sendText = async (text) => {
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    // Offer handoff if the visitor asks for a person
    if (PERSON_HINTS.some((h) => text.toLowerCase().includes(h))) {
      setHform((f) => ({ ...f, question: f.question || text }));
      setHandoff("form");
    }

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + chunk };
          return next;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: "I'm having trouble responding right now. You can leave your details and the team will follow up.",
        };
        return next;
      });
      setHandoff("form");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText(input.trim());
    }
  };

  const submitHandoff = async (e) => {
    e.preventDefault();
    if (!hform.name.trim() || !hform.email.trim() || !hform.question.trim()) return;
    try {
      const res = await fetch(`${API}/handoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(hform),
      });
      if (!res.ok) throw new Error("failed");
      setHandoff("done");
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Thank you, ${hform.name.split(" ")[0]}. I've passed your details to the team, and they will be in touch by email soon.` },
      ]);
      setHform({ name: "", email: "", question: "" });
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Something went wrong passing your details along. Please email weseeyou@melanointernational.com directly." },
      ]);
    }
  };

  const showStarters = messages.length === 1 && !handoff;

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close concierge" : "Open concierge"}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full gold-gradient-bg text-[#0a0908] flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:brightness-110 transition"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <div
        className={`fixed bottom-24 right-6 z-[60] w-[92vw] max-w-[380px] origin-bottom-right transition-all duration-300 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <div className="rounded-xl overflow-hidden border border-gold/30 bg-[#0a0908] shadow-2xl flex flex-col h-[540px] max-h-[74vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-[#0d0b09]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full border border-gold/50 flex items-center justify-center">
                <Sparkles size={16} className="text-gold" />
              </div>
              <div>
                <p className="font-display uppercase tracking-[0.16em] text-sm text-white leading-none">Concierge</p>
                <p className="text-white/45 text-xs mt-1">Melano International</p>
              </div>
            </div>
            <button
              onClick={() => { setHandoff("form"); setHform((f) => ({ ...f, question: f.question })); }}
              className="inline-flex items-center gap-1.5 text-gold text-xs font-display uppercase tracking-[0.14em] hover:brightness-110 transition"
            >
              <UserRound size={14} /> Talk to a person
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-melano-purple text-white rounded-br-sm"
                      : "bg-[#151109] text-white/85 border border-border rounded-bl-sm"
                  }`}
                >
                  {m.content || (busy ? "\u2026" : "")}
                </div>
              </div>
            ))}

            {showStarters && (
              <div className="flex flex-wrap gap-2 pt-1">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendText(s)}
                    className="text-left text-xs text-gold border border-gold/40 rounded-full px-3 py-1.5 hover:gold-gradient-bg hover:text-[#0a0908] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {handoff === "form" && (
              <form onSubmit={submitHandoff} className="rounded-lg border border-gold/40 bg-[#0d0b09] p-4 space-y-3">
                <p className="eyebrow">Leave your details</p>
                <input
                  value={hform.name}
                  onChange={(e) => setHform({ ...hform, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-gold"
                />
                <input
                  type="email"
                  value={hform.email}
                  onChange={(e) => setHform({ ...hform, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-gold"
                />
                <textarea
                  rows={2}
                  value={hform.question}
                  onChange={(e) => setHform({ ...hform, question: e.target.value })}
                  placeholder="What would you like to ask?"
                  className="w-full resize-none bg-transparent border border-border rounded-sm px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-gold"
                />
                <div className="flex gap-2">
                  <button type="submit" className="flex-1 gold-gradient-bg text-[#0a0908] font-display uppercase font-bold tracking-[0.14em] text-xs py-2.5 rounded-sm hover:brightness-110 transition">
                    Send to team
                  </button>
                  <button type="button" onClick={() => setHandoff(null)} className="px-3 text-white/50 text-xs uppercase tracking-[0.14em] font-display hover:text-white/80 transition">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border p-3 bg-[#0d0b09]">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Ask the concierge\u2026"
                className="flex-1 resize-none bg-transparent border border-border rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-gold transition-colors max-h-28"
              />
              <button
                onClick={() => sendText(input.trim())}
                disabled={busy || !input.trim()}
                aria-label="Send"
                className="w-10 h-10 shrink-0 rounded-lg gold-gradient-bg text-[#0a0908] flex items-center justify-center hover:brightness-110 transition disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
